import { useCallback, useEffect, useState } from 'react';
import {
  opsApi,
  type OpsRiskDto, type OpsRiskInput, type OpsRiskStatus, type OpsStaffSummaryDto,
} from '../api';
import { OpsPanelState, OpsTable, RowActions, opsErrorText, useOpsPanel } from './ops/panel';

/**
 * TICK-123 — the risk register, and the sign-off that replaced a random string.
 *
 * Three things this screen must not do, each of them a rule the server already enforces:
 *
 * - **It never sends a score.** `score` is `likelihood × impact`, computed server-side; the form
 *   shows the product as it changes so the operator can see what they are choosing, and posts only
 *   the two factors. A stored score that disagrees with its own factors is the defect the contract
 *   calls `derived` to prevent.
 * - **It has no signer field.** Who signed is whoever the token belongs to. The pair this replaces
 *   (`signature_token`, `pin_hash`) let the client name the signer, which is not evidence of
 *   anything — and `pin_hash` stored four plaintext digits under a name that claimed otherwise.
 * - **It does not offer to delete a signed-off risk.** The server refuses; the screen says why, and
 *   points at closing it as `resolved` instead, which is what keeps the sign-off history intact.
 *
 * Sign-off is owner-only on the server. Staff see the register and the history; the button that
 * accepts a risk on the organisation's behalf is not theirs.
 *
 * TICK-136 S3 — the owner column reads `ownerName` first and falls back to `importedOwnerName`,
 * the same two-value shape `OpsTasksPanel` has had since TICK-122. Before 0068 these two tables had
 * only the reference, so an import that could not match a name had nowhere to put it; now an
 * unmatched row says whose name came in and that nobody is attached to it yet.
 */

const STATUS_LABEL: Record<OpsRiskStatus, string> = {
  open: 'เปิดอยู่',
  mitigating: 'กำลังลดความเสี่ยง',
  accepted_signed_off: 'ยอมรับแล้ว (ลงนาม)',
  resolved: 'ปิดแล้ว',
};

/** `accepted_signed_off` is absent: it is reached by signing, never by picking it from a menu. */
const SELECTABLE_STATUS: OpsRiskStatus[] = ['open', 'mitigating', 'resolved'];
const SCALE = [1, 2, 3, 4, 5];

export function OpsRisksPanel({ token, eventSlug, role }: { token: string; eventSlug?: string; role: string | null }) {
  const [risks, setRisks] = useState<OpsRiskDto[] | null>(null);
  const [crew, setCrew] = useState<OpsStaffSummaryDto[]>([]);
  const [loadError, setLoadError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<OpsRiskDto | null>(null);
  const [signing, setSigning] = useState<OpsRiskDto | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        opsApi.listRisks(token, eventSlug),
        opsApi.listStaff(token, eventSlug),
      ]);
      setRisks(r);
      setCrew(s);
    } catch (e) {
      setLoadError(opsErrorText(e, 'โหลดทะเบียนความเสี่ยงไม่สำเร็จ'));
    }
  }, [token, eventSlug]);

  useEffect(() => { void load(); }, [load]);

  const { busy, error, run } = useOpsPanel(load);

  if (!risks) return <OpsPanelState error={loadError} />;

  return (
    <div className="ops-panel">
      {error && <div className="form-error">{error}</div>}

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--dim)', lineHeight: 1.7 }}>
          คะแนน = โอกาสเกิด × ผลกระทบ คำนวณโดยระบบ ไม่ได้กรอก · การลงนามยอมรับบันทึกว่า “ใคร” จากบัญชีที่ล็อกอินอยู่
        </p>
        <button className="btn-primary" disabled={busy} onClick={() => setCreating(true)}>เพิ่มความเสี่ยง</button>
      </div>

      {risks.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีความเสี่ยงในทะเบียนของอีเวนต์นี้</div>
      ) : (
        <OpsTable head={<><th>ความเสี่ยง</th><th>หมวด</th><th>เจ้าของ</th><th className="num">โอกาส × ผล</th><th className="num">คะแนน</th><th>สถานะ</th><th>การลงนาม</th><th /></>}>
              {risks.map((r) => (
                <tr key={r.id}>
                  <td>{r.title}</td>
                  <td style={{ color: 'var(--dim)' }}>{r.category}</td>
                  <td>{r.ownerName ?? (r.importedOwnerName
                      ? <span style={{ color: 'var(--dim)' }} title="ชื่อจากการนำเข้า ยังไม่ได้ผูกกับคนในทีมงาน">{r.importedOwnerName} · ยังไม่ผูก</span>
                      : <span style={{ color: 'var(--dim)' }}>—</span>)}</td>
                  <td className="num" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{r.likelihood} × {r.impact}</td>
                  <td className="num" style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{r.score}</td>
                  <td>{STATUS_LABEL[r.status]}</td>
                  <td style={{ fontSize: 11.5, color: 'var(--dim)' }}>
                    {r.signoffs.length === 0
                      ? '—'
                      : r.signoffs.map((s) => `${s.actor} · ${s.signedAt.slice(0, 10)}`).join(' / ')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
                      <button className="btn-link" disabled={busy} onClick={() => setEditing(r)}>แก้ไข</button>
                      {role === 'owner' && r.status !== 'accepted_signed_off' && (
                        <button className="btn-link" disabled={busy} onClick={() => setSigning(r)}>ลงนามยอมรับ</button>
                      )}
                      <button
                        className="btn-link"
                        disabled={busy}
                        onClick={() => {
                          if (!confirm(`ลบความเสี่ยง "${r.title}"?`)) return;
                          void run(() => opsApi.deleteRisk(token, r.id, r.version, eventSlug));
                        }}
                      >
                        ลบ
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
        </OpsTable>
      )}

      {(creating || editing) && (
        <RiskEditor
          risk={editing}
          crew={crew}
          busy={busy}
          onCancel={() => { setCreating(false); setEditing(null); }}
          onSave={async (input) => {
            const ok = await run(() => (editing
              ? opsApi.updateRisk(token, editing.id, { ...input, version: editing.version }, eventSlug)
              : opsApi.createRisk(token, input, eventSlug)));
            if (ok) { setCreating(false); setEditing(null); }
          }}
        />
      )}

      {signing && (
        <SignoffForm
          risk={signing}
          busy={busy}
          onCancel={() => setSigning(null)}
          onSign={async (input) => {
            const ok = await run(() => opsApi.signOffRisk(token, signing.id, input, eventSlug));
            if (ok) setSigning(null);
          }}
        />
      )}
    </div>
  );
}

function RiskEditor({ risk, crew, busy, onCancel, onSave }: {
  risk: OpsRiskDto | null;
  crew: OpsStaffSummaryDto[];
  busy: boolean;
  onCancel: () => void;
  onSave: (input: OpsRiskInput) => void;
}) {
  const [title, setTitle] = useState(risk?.title ?? '');
  const [category, setCategory] = useState(risk?.category ?? '');
  const [likelihood, setLikelihood] = useState(risk?.likelihood ?? 3);
  const [impact, setImpact] = useState(risk?.impact ?? 3);
  const [status, setStatus] = useState<OpsRiskStatus>(risk?.status ?? 'open');
  const [ownerStaffId, setOwnerStaffId] = useState(risk?.ownerStaffId ?? '');
  const [mitigationPlan, setMitigationPlan] = useState(risk?.mitigationPlan ?? '');

  const signedOff = risk?.status === 'accepted_signed_off';

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px' }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13.5 }}>{risk ? `แก้ไข ${risk.title}` : 'เพิ่มความเสี่ยง'}</h4>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }}>
        ความเสี่ยง
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>

      <div className="ticket-edit-grid" style={{ marginTop: 12 }}>
        <label>
          หมวด
          <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="เช่น สภาพอากาศ, ความปลอดภัย" />
        </label>
        <label>
          เจ้าของความเสี่ยง
          <select value={ownerStaffId} onChange={(e) => setOwnerStaffId(e.target.value)}>
            <option value="">— ไม่ระบุ —</option>
            {crew.map((m) => <option key={m.id} value={m.id}>{m.displayName}</option>)}
          </select>
        </label>
        <label>
          โอกาสเกิด (1–5)
          <select value={likelihood} onChange={(e) => setLikelihood(Number(e.target.value))}>
            {SCALE.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>
          ผลกระทบ (1–5)
          <select value={impact} onChange={(e) => setImpact(Number(e.target.value))}>
            {SCALE.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>
          สถานะ
          <select
            value={status}
            disabled={signedOff}
            onChange={(e) => setStatus(e.target.value as OpsRiskStatus)}
          >
            {SELECTABLE_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            {signedOff && <option value="accepted_signed_off">{STATUS_LABEL.accepted_signed_off}</option>}
          </select>
        </label>
      </div>

      {/* Shown, never sent: the product is what the server will compute from the two factors above. */}
      <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 8 }}>
        คะแนนที่ระบบจะคำนวณ: <strong style={{ fontFamily: 'var(--mono)', color: 'var(--fg)' }}>{likelihood * impact}</strong>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, marginTop: 12 }}>
        แผนลดความเสี่ยง
        <textarea rows={2} value={mitigationPlan} onChange={(e) => setMitigationPlan(e.target.value)} />
      </label>

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button
          className="btn-primary"
          disabled={busy || !title.trim() || !category.trim()}
          onClick={() => onSave({
            title: title.trim(),
            category: category.trim(),
            likelihood,
            impact,
            // A signed-off risk keeps its status; sending it back unchanged is not a state change.
            status,
            ownerStaffId: ownerStaffId || null,
            mitigationPlan: mitigationPlan.trim(),
          })}
        >
          บันทึก
        </button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}

function SignoffForm({ risk, busy, onCancel, onSign }: {
  risk: OpsRiskDto;
  busy: boolean;
  onCancel: () => void;
  onSign: (input: { waiverReason?: string; conditions?: string }) => void;
}) {
  const [waiverReason, setWaiverReason] = useState('');
  const [conditions, setConditions] = useState('');

  return (
    <div style={{ border: '1px solid rgba(224,168,107,.35)', borderRadius: 10, padding: '14px 16px' }}>
      <h4 style={{ margin: '0 0 4px', fontSize: 13.5 }}>ลงนามยอมรับ “{risk.title}”</h4>
      <p style={{ margin: '0 0 12px', fontSize: 11.5, color: 'var(--dim)', lineHeight: 1.7 }}>
        ระบบบันทึกว่าเป็นบัญชีที่ล็อกอินอยู่ตอนนี้ ไม่มีช่องให้พิมพ์ชื่อผู้ลงนาม · เมื่อลงนามแล้ว
        ความเสี่ยงนี้จะลบไม่ได้อีก ปิดได้ด้วยการตั้งสถานะเป็น “ปิดแล้ว” เท่านั้น
      </p>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }}>
        เหตุผลที่ยอมรับ
        <textarea rows={2} value={waiverReason} onChange={(e) => setWaiverReason(e.target.value)} />
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, marginTop: 10 }}>
        เงื่อนไขที่แนบไปกับการยอมรับ
        <textarea rows={2} value={conditions} onChange={(e) => setConditions(e.target.value)} />
      </label>

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button
          className="btn-primary"
          disabled={busy}
          onClick={() => onSign({ waiverReason: waiverReason.trim(), conditions: conditions.trim() })}
        >
          ลงนามยอมรับ
        </button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}
